"use client"

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import debounce from "lodash.debounce"
import { api } from "../../services/Axios"
import {
  FaTags,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
} from "react-icons/fa"
import PageHeader from "../ui/management/PageHeader"
import StatsCard from "../ui/management/StatsCard"
import Pagination from "../ui/management/Pagination"
import Loader from "../utils/loader/Loader"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import ReusableModal from "../utils/modals/ReusableModal"

export interface Category {
  _id: string
  nombre: string
  descripcion: string
}

function ManageCategories() {
  // Estados para el listado y la paginación
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [currentPage, setCurrentPage] = useState<number>(1)
  const categoriesPerPage = 6
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalCategories, setTotalCategories] = useState<number>(0)

  // Estados para los modales y el modo de edición
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [isReadOnlyMode, setIsReadOnlyMode] = useState<boolean>(false)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)

  // Estados para modal de confirmación y mensajes
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string>("")
  const [modalAction, setModalAction] = useState<(() => void) | null>(null)
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
  const [messageContent, setMessageContent] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error" | "info" | "warning">("success")

  // Estados para los campos del formulario
  const [nombre, setNombre] = useState<string>("")
  const [descripcion, setDescripcion] = useState<string>("")
  const [nombreError, setNombreError] = useState<string>("")
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // Referencia para el input de búsqueda
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Función para obtener categorías desde la API con paginación y búsqueda
  const fetchCategories = useCallback(
    (page: number, search: string) => {
      setIsLoading(true)
      api
        .get("/api/categorias", {
          params: {
            page,
            limit: categoriesPerPage,
            searchTerm: search || undefined,
          },
        })
        .then((response) => {
          const { categorias, totalPages, totalCategorias } = response.data
          setCategories(categorias)
          setTotalPages(totalPages)
          setTotalCategories(totalCategorias)
          setCurrentPage(page)
        })
        .catch((error) => console.error("Error al obtener categorías:", error))
        .finally(() => setIsLoading(false))
    },
    [categoriesPerPage],
  )

  useEffect(() => {
    fetchCategories(currentPage, searchTerm)
  }, [fetchCategories, currentPage, searchTerm])

  // Debounce para la búsqueda
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value)
        setCurrentPage(1)
      }, 500),
    [],
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setCurrentPage(1)
    if (searchInputRef.current) {
      searchInputRef.current.value = ""
    }
    fetchCategories(1, "")
  }

  const clearErrors = () => {
    setNombreError("")
  }

  // Funciones para el modal de confirmación
  const openConfirmationModal = (message: string, action?: () => void) => {
    setModalMessage(message)
    setModalAction(() => action || closeConfirmationModal)
    setModalOpen(true)
  }

  const closeConfirmationModal = () => {
    setModalOpen(false)
    setModalAction(null)
    setModalMessage("")
  }

  // Acciones para agregar, ver y editar categoría
  const handleAddCategory = () => {
    setIsEditMode(false)
    clearErrors()
    setNombre("")
    setDescripcion("")
    setIsModalOpen(true)
  }

  const handleViewCategory = (category: Category) => {
    setSelectedCategory(category)
    setNombre(category.nombre)
    setDescripcion(category.descripcion)
    setIsEditMode(false)
    setIsReadOnlyMode(true)
    clearErrors()
    setIsModalOpen(true)
  }

  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category)
    setNombre(category.nombre)
    setDescripcion(category.descripcion)
    clearErrors()
    setIsEditMode(true)
    setIsModalOpen(true)
  }

  const handleDeleteCategory = (categoryId: string) => {
    openConfirmationModal("¿Está seguro de que desea eliminar esta categoría?", () => {
      api
        .delete(`/api/categorias/${categoryId}`)
        .then(() => {
          setMessageContent("Categoría eliminada exitosamente.")
          setMessageType("success")
          setShowMessageModal(true)
          fetchCategories(currentPage, searchTerm)
        })
        .catch(() => {
          setMessageContent("Error al eliminar la categoría.")
          setMessageType("error")
          setShowMessageModal(true)
        })
        .finally(() => closeConfirmationModal())
    })
  }

  // Acción para confirmar el guardado de la categoría
  const handleConfirmSaveCategory = () => {
    if (!nombre.trim()) {
      setNombreError("Nombre es obligatorio")
      return
    }
    openConfirmationModal(
      isEditMode
        ? "¿Desea guardar los cambios en esta categoría?"
        : "¿Desea agregar esta categoría?",
      handleSaveCategory,
    )
  }

  const handleSaveCategory = () => {
    if (isSaving) return; // 🚫 Si ya está guardando, no hace nada
    setIsSaving(true)
    const categoryData = { nombre, descripcion }
    const action = isEditMode && selectedCategory
      ? api.put(`/api/categorias/${selectedCategory._id}`, categoryData)
      : api.post("/api/categorias/add", categoryData)

    action
      .then(() => {
        setMessageContent(
          isEditMode
            ? "Categoría actualizada exitosamente."
            : "Categoría agregada exitosamente.",
        )
        setMessageType("success")
        setShowMessageModal(true)
        setIsModalOpen(false)
        fetchCategories(currentPage, searchTerm)
      })
      .catch((error) => {
        const errorMsg = error.response?.data?.msg || "Error al guardar la categoría."
        setMessageContent(errorMsg)
        setMessageType("error")
        setShowMessageModal(true)
      })
      .finally(() => {
        setIsSaving(false)
        closeConfirmationModal()
      })
  }

  const handleCloseMessageModal = () => {
    setShowMessageModal(false)
    closeConfirmationModal()
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {isLoading && <Loader />}
      <ConfirmationModal
        isOpen={modalOpen}
        title="Confirmación"
        message={modalMessage}
        onConfirm={modalAction || closeConfirmationModal}
        onCancel={closeConfirmationModal}
        isProcessing={isSaving}
      />
      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={handleCloseMessageModal}
      />

      {/* Encabezado de la página */}
      <PageHeader
        title="Gestión de Categorías"
        subtitle="Administra las categorías de tus productos"
        icon={FaTags}
        actionLabel="Agregar Categoría"
        onAction={handleAddCategory}
        actionIcon={FaPlus}
      />

      {/* Sección de filtros y búsqueda */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Categorías
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre"
            onChange={handleSearchChange}
            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
            ref={searchInputRef}
          />
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Total Categorías"
          value={totalCategories.toString()}
          description="Categorías registradas"
          icon={FaTags}
          gradient={false}
        />
      </div>

      {/* Lista de categorías en forma de tabla */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {categories.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{category.nombre}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.descripcion}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleViewCategory(category)}
                          title="Ver Categoría"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="bg-yellow-100 text-yellow-600 hover:bg-yellow-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleEditCategory(category)}
                          title="Editar Categoría"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleDeleteCategory(category._id)}
                          title="Eliminar Categoría"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaTags className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron categorías</h3>
            <p className="text-gray-500 mb-4">
              Intenta con otra búsqueda o agrega una nueva categoría
            </p>
            <button
              onClick={handleAddCategory}
              className="flex items-center px-4 py-2 bg-[#2d53da] hover:bg-[#2d53da]/90 text-white rounded-lg transition-colors duration-200"
            >
              <FaPlus className="mr-2" /> Agregar Categoría
            </button>
          </div>
        )}
      </div>

      {/* Paginación */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Modal para agregar/editar o ver categoría */}
      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setIsReadOnlyMode(false)
        }}
        title={
          isReadOnlyMode
            ? "Ver Categoría"
            : isEditMode
            ? "Editar Categoría"
            : "Agregar Categoría"
        }
        fields={[
          {
            label: "Nombre",
            value: nombre,
            onChange: setNombre,
            required: true,
            error: nombreError,
            readOnly: isReadOnlyMode,
          },
          {
            label: "Descripción",
            value: descripcion,
            onChange: setDescripcion,
            readOnly: isReadOnlyMode,
          },
        ]}
        onSave={handleConfirmSaveCategory}
        isSaving={isSaving}
        saveButtonText={isEditMode ? "Guardar Cambios" : "Agregar Categoría"}
        readOnlyMode={isReadOnlyMode}
      />
    </div>
  )
}

export default ManageCategories
